import { Badge, Button, Center, VStack, Flex, HStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { LucideBox, MapPinIcon } from 'lucide-react';
import { EnrolmentFormModal } from '@duke-hq/app-provider-web/tenant/Enrolments/EnrolmentFormModal';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { StudentEnrolmentCard } from '@duke-hq/enrolments-ui/components/StudentEnrolmentCard';
import { canReferToCourse } from './canReferToCourse';

function ReferToCourseButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Button onClick={onOpen} leftIcon={<LucideBox />}>
      <Trans>Refer to course</Trans>
    </Button>
  );
}

export function StudentEnrolments({
  student,
}: {
  student: NonNullable<RouterOutputs['users']['studentById']>;
}): React.ReactElement {
  const { user } = useAuthTenant();
  const { data: enrolments = [] } = trpcTenant.enrolments.getByUserId.useQuery({
    user_id: student.id,
  });

  const isReferToCourseEnabledValue = canReferToCourse(student, enrolments);

  if (enrolments != null && enrolments.length > 0) {
    return (
      <Flex direction="column" w="full" h="full">
        <VStack p={4} spacing={4}>
          {enrolments.flatMap((enrolment) => {
            if (enrolment.course == null) {
              return [];
            }
            return (
              <StudentEnrolmentCard
                key={enrolment.id}
                enrolmentId={enrolment.id}
                enrolmentStatus={enrolment.status}
                course={enrolment.course}
                consultant={enrolment.consultant}
                consultantSuffix={enrolment.consultant?.id === user.id ? ' (You)' : undefined}
                extraTags={
                  enrolment.siteLocation != null ? (
                    <Badge colorScheme="gray" variant="subtle" textTransform="none">
                      <HStack spacing={1}>
                        <MapPinIcon size={12} />
                        {enrolment.siteLocation.name}
                      </HStack>
                    </Badge>
                  ) : null
                }
              />
            );
          })}
        </VStack>
        {isReferToCourseEnabledValue === true ? (
          <Center>
            <VStack spacing={2} py={4}>
              <EnrolmentFormModal
                student={student}
                referral={Boolean(student)}
                button={ReferToCourseButton}
              />
            </VStack>
          </Center>
        ) : null}
      </Flex>
    );
  }

  return (
    <Center minH="200px">
      <VStack spacing={2}>
        <Trans>No enrolments available</Trans>
        {isReferToCourseEnabledValue === true ? (
          <EnrolmentFormModal
            student={student}
            referral={Boolean(student)}
            button={ReferToCourseButton}
          />
        ) : null}
      </VStack>
    </Center>
  );
}
